import { useState, useEffect, useCallback } from 'react'
import { useAutoSave } from './useAutoSave'

/**
 * Hook for managing tool form data with auto-save.
 * Provides controlled-component-friendly state management.
 */
export function useToolData(toolId: string) {
  const { load, save } = useAutoSave(toolId)
  const [data, setData] = useState<Record<string, unknown>>({})
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)

    load().then((loaded) => {
      if (!cancelled) {
        setData(loaded)
        setIsLoading(false)
      }
    })

    return () => { cancelled = true }
  }, [load])

  const setField = useCallback(
    (key: string, value: unknown) => {
      setData((prev) => {
        const next = { ...prev, [key]: value }
        save(next)
        return next
      })
    },
    [save],
  )

  const getField = useCallback(
    (key: string, fallback: unknown = ''): unknown => {
      return data[key] ?? fallback
    },
    [data],
  )

  const clearAll = useCallback(() => {
    setData({})
    save({})
  }, [save])

  return { data, setField, getField, isLoading, clearAll }
}
